import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, Sparkles, TriangleAlert, X } from "lucide-react";
import { NAV_RUBRIQUES, type NavItem } from "../../helpers/navCatalog.generated";
import { NAV_ICONS } from "../../helpers/navIcons";
import { useAuth } from "../../helpers/useAuth";
import { useLicenseStatus } from "../../helpers/useLicenseStatus";
import { rubriqueForPath } from "../../nav/navCatalogHelpers";
import { EspaceMenu } from "./EspaceMenu";
import { NavSearch } from "./NavSearch";
import { SiteTabbar } from "./SiteTabbar";
import { chargerDatesTirages, dateDeLaPage, monterCalendrier } from "../../nav/searchPanel";

const NUMEROS = Array.from({ length: 56 }, (_, i) => i + 1);

/** Étiquette d'accès d'un lien du menu ; aucune pour un abonné Premium (décision du 22/10/2026). */
const Acces = ({ tier, premium }: { tier: NavItem["tier"]; premium: boolean }) =>
  premium ? null
  : tier === "account" ? <span className="km-acces km-acces--account">Compte gratuit</span>
  : tier === "premium" ? <span className="km-acces km-acces--premium">Premium</span>
  : null;

/** Lien interne : <a> pour une page serveur, <Link> pour une page de l'application. */
const NavLink = ({ item, rubrique, children, className }: { item: Pick<NavItem, "href" | "isStatic" | "tier">; rubrique: string; children: React.ReactNode; className?: string }) => {
  const props = { className, "data-nav-rubrique": rubrique, "data-requires": item.tier ?? undefined };
  return item.isStatic ? <a href={item.href} {...props}>{children}</a> : <Link to={item.href} {...props}>{children}</Link>;
};

export const SiteHeader = ({ onOpenCalendar }: { onOpenCalendar: () => void }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { data: licence } = useLicenseStatus();
  const etat = licence?.etat ?? (licence?.hasFullAccess ? "premium" : "gratuit");
  const connecte = authState?.type === "authenticated";
  const active = rubriqueForPath(location.pathname);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [tiroir, setTiroir] = useState(false);
  const recherche = useRef<HTMLDialogElement>(null);
  const aller = (href: string) => { recherche.current?.close(); href.startsWith("/numero/") || href.startsWith("/tirage/") ? window.location.assign(href) : navigate(href); };
  const calRoot = useRef<HTMLDivElement>(null);
  const calendrier = useRef<ReturnType<typeof monterCalendrier> | null>(null);
  const ouvrirRecherche = () => {
    if (!recherche.current?.showModal) return;
    recherche.current.showModal();
    recherche.current.querySelector<HTMLInputElement>("input")?.focus();
    if (!calendrier.current && calRoot.current) calendrier.current = monterCalendrier(calRoot.current, { fetchDates: () => chargerDatesTirages(), courante: dateDeLaPage(location.pathname) });
    void calendrier.current?.ouvrir();
  };

  useEffect(() => { setOuvert(null); setTiroir(false); }, [location.pathname]);

  return (
    <>
      <a className="mag-skip-link" href="#contenu">Aller au contenu</a>
      <header className="km-hd" role="banner">
        <div className="km-hd__in">
          {/* L'accueil public est une page serveur (Twig) : lien complet, jamais une route de l'application
              (le routeur React n'a pas de « / » et affichait une 404). /home/ est une route de l'application. */}
          {connecte ? (
            <Link to="/home/" className="km-hd__logo" aria-label="KenoMetric, accueil">
              <img src="/logo-banner.webp" alt="KenoMetric" width={148} height={36} fetchPriority="high" />
            </Link>
          ) : (
            <a href="/" className="km-hd__logo" aria-label="KenoMetric, accueil">
              <img src="/logo-banner.webp" alt="KenoMetric" width={148} height={36} fetchPriority="high" />
            </a>
          )}
          <nav className="km-nav" aria-label="Rubriques" data-nav-emplacement="menu">
            <ul className="km-nav__list">
              {NAV_RUBRIQUES.map((r) => (
                <li key={r.key} className={`km-nav__item${ouvert === r.key ? " is-open" : ""}`}
                  onMouseEnter={() => setOuvert(r.key)} onMouseLeave={() => setOuvert(null)}
                  onKeyDown={(e) => { if (e.key === "Escape") setOuvert(null); }}
                  onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOuvert(null); }}>
                  <a className="km-nav__hub" href={r.hubPath} data-nav-rubrique={r.key}
                    aria-expanded={ouvert === r.key} aria-controls={`km-menu-${r.key}`}
                    aria-current={active === r.key ? "page" : undefined}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" && ouvert !== r.key) || e.key === "ArrowDown") {
                        e.preventDefault();
                        setOuvert(r.key);
                        requestAnimationFrame(() => document.querySelector<HTMLAnchorElement>(`#km-menu-${r.key} a`)?.focus());
                      }
                    }}>{r.label}</a>
                  <div className="km-nav__panel" id={`km-menu-${r.key}`}>
                    <p className="km-nav__desc">{r.desc}</p>
                    <ul className="km-nav__links">
                      {r.menu.map((item) => (
                        <li key={item.href}>
                          <NavLink item={item} rubrique={r.key}>
                            <span className="km-nav__label">{item.label}<Acces tier={item.tier} premium={licence?.hasFullAccess === true} /></span>
                            <span className="km-nav__sub">{item.desc}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                    <div className="km-nav__foot">
                      {/* « Tout voir » : calculé par NavCatalog, sans doublon avec les liens du menu. */}
                      {r.toutVoir && <a className="km-nav__all" href={r.toutVoir} data-nav-rubrique={r.key}>{r.toutVoirLabel}</a>}
                      {r.key === "resultats" && <button type="button" className="km-nav__action" onClick={onOpenCalendar}>Choisir une date</button>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </nav>
          <div className="km-hd__sp" />
          <button type="button" className="km-search km-search--btn" data-km-search-field aria-haspopup="dialog" aria-controls="km-search-dialog" onClick={ouvrirRecherche}><Search aria-hidden="true" /><span>Numéro ou date</span></button>
          <div className="km-hd__cta">
            {!connecte && <><Link className="km-hd__lnk km-hd__tarifs" to="/tarifs/?cta_source=header_tarifs">Tarifs</Link><Link className="km-hd__lnk" to="/login/">Connexion</Link><a className="km-btn" href="/inscription/?cta_source=header">Compte gratuit</a></>}
            {connecte && <Link className="km-hd__lnk km-hd__mk" to="/mon-keno/">Mes grilles</Link>}
            {/* Un seul élément d'état (D1) : Premium pour le gratuit, pastille d'essai ou d'impayé, rien pour un abonné. */}
            {connecte && licence && etat === "gratuit" && <Link className="km-btn" data-auth="free" to="/tarifs/?cta_source=header">Premium</Link>}
            {connecte && etat === "essai" && <Link className="km-pstate" to="/compte/"><Sparkles aria-hidden="true" />Essai Premium · J-{licence?.trialDaysRemaining ?? 14}</Link>}
            {connecte && etat === "impaye" && <Link className="km-pstate km-pstate--warn" to="/compte/"><TriangleAlert aria-hidden="true" />Paiement à régulariser</Link>}
            {connecte && <EspaceMenu />}
          </div>
          <button type="button" className="km-hd__icon" data-km-search-open aria-label="Chercher un numéro ou une date" onClick={ouvrirRecherche}><Search aria-hidden="true" /></button>
          {/* Connecté : les rubriques sont dans le menu Mon espace, plus de tiroir. */}
          {!connecte && <button type="button" className="km-hd__icon km-hd__burger" data-km-burger aria-expanded={tiroir} aria-controls="km-drawer" aria-label="Ouvrir le menu" onClick={() => setTiroir(true)}><Menu aria-hidden="true" /></button>}
        </div>
      </header>
      <div className="km-drawer" id="km-drawer" hidden={!tiroir}>
        <div className="km-drawer__backdrop" onClick={() => setTiroir(false)} />
        <div className="km-drawer__panel is-react" role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={(e) => { if (e.key === "Escape") setTiroir(false); }}>
          <div className="km-drawer__head"><span className="km-drawer__title">Menu</span>
            <button type="button" className="km-hd__icon" aria-label="Fermer le menu" onClick={() => setTiroir(false)}><X aria-hidden="true" /></button></div>
          <nav aria-label="Rubriques" data-nav-emplacement="menu_mobile">
            {NAV_RUBRIQUES.map((r) => {
              const Icon = NAV_ICONS[r.icon];
              return <a key={r.key} className="km-drawer__rub" href={r.hubPath} data-nav-rubrique={r.key}>{Icon && <Icon aria-hidden="true" />}<span>{r.label}</span><small>{r.desc}</small></a>;
            })}
          </nav>
          <div className="km-drawer__cta">
            {!connecte && <><a className="km-btn" href="/inscription/?cta_source=menu_mobile">Compte gratuit</a><Link className="km-hd__lnk" to="/login/">Connexion</Link><Link className="km-hd__lnk" to="/tarifs/?cta_source=menu_mobile_tarifs">Tarifs</Link></>}
          </div>
        </div>
      </div>
      <SiteTabbar active={active} onOpenSearch={ouvrirRecherche} connecte={connecte} />
      <dialog className="km-sover km-sp" id="km-search-dialog" ref={recherche} aria-labelledby="km-sp-titre" onClick={(e) => { if (e.target === recherche.current) recherche.current?.close(); }}>
        <div className="km-sp__head"><p className="km-sp__titre" id="km-sp-titre">Rechercher</p>
          <button type="button" className="km-hd__icon" aria-label="Fermer la recherche" onClick={() => recherche.current?.close()}><X aria-hidden="true" /></button></div>
        <NavSearch onNavigate={aller} autoFocus />
        <section className="km-sp__sec" aria-labelledby="km-sp-numeros"><p className="km-sp__h" id="km-sp-numeros">Les 56 numéros</p>
          <div className="km-sp__nums">{NUMEROS.map((n) => <a key={n} className="km-sp__num" href={`/numero/${n}/`} aria-label={`Fiche du numéro ${n}`}>{n}</a>)}</div></section>
        <section className="km-sp__sec" aria-labelledby="km-sp-tirage"><p className="km-sp__h" id="km-sp-tirage">Un tirage</p>
          <div className="km-sp__raccourcis"><a href="/tirage-du-jour/">Dernier tirage</a><a href="/derniers-tirages-keno/">Tous les tirages</a></div>
          <div ref={calRoot} /></section>
      </dialog>
    </>
  );
};
